/**
 * IntegrationBadge
 *
 * Generic badge showing a platform connection status on domain pages.
 * Designed to scale — add new platforms by extending the icon/color map.
 *
 * Linked: green dot + platform name (e.g., "HubSpot")
 * Unlinked: muted "Link to {platform}" prompt
 */

import { Link } from 'react-router-dom'
import { cn } from '@/utils/cn'
import type { IntegrationPlatform } from '@/hooks/useIntegrations'

// Platform display config — extend this when adding Shopify, WordPress, etc.
const platformConfig: Record<IntegrationPlatform, {
  label: string
  icon: string  // emoji or short text fallback; can be replaced with SVG later
  color: string // accent color for the badge text
  iconBg: string // compact icon background + text color
}> = {
  hubspot: { label: 'HubSpot', icon: '⬡', color: 'text-orange-600 dark:text-orange-400', iconBg: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' },
  shopify: { label: 'Shopify', icon: '🛍', color: 'text-green-600 dark:text-green-400', iconBg: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
  wordpress: { label: 'WordPress', icon: 'W', color: 'text-blue-600 dark:text-blue-400', iconBg: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
}

interface IntegrationBadgeProps {
  platform: IntegrationPlatform
  isLinked: boolean
  portalName?: string | null
  size?: 'sm' | 'md'
  className?: string
}

export default function IntegrationBadge({
  platform,
  isLinked,
  portalName,
  size = 'md',
  className,
}: IntegrationBadgeProps) {
  const config = platformConfig[platform]

  if (isLinked) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border',
          size === 'sm'
            ? 'px-1.5 py-0.5 text-[10px]'
            : 'px-2.5 py-1 text-xs',
          'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300',
          className
        )}
        title={portalName ? `Connected to ${config.label}: ${portalName}` : `Connected to ${config.label}`}
      >
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-50" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
        </span>
        <span className={cn('font-medium', config.color)}>{config.label}</span>
      </span>
    )
  }

  // Unlinked — show "Link to {platform}" prompt
  return (
    <Link
      to="/settings/integrations"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border',
        size === 'sm'
          ? 'px-1.5 py-0.5 text-[10px]'
          : 'px-2.5 py-1 text-xs',
        'bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors',
        className
      )}
      title={`Link this domain to ${config.label}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
      <span className="font-medium">Link to {config.label}</span>
    </Link>
  )
}

/** Compact icon-only version for dashboard cards */
export function IntegrationIcon({
  platform,
  isLinked,
  portalName,
  className,
}: {
  platform: IntegrationPlatform
  isLinked: boolean
  portalName?: string | null
  className?: string
}) {
  const config = platformConfig[platform]

  if (!isLinked) return null // Only show linked integrations on dashboard cards

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center w-4 h-4 rounded-sm text-[9px] font-bold',
        config.iconBg,
        className
      )}
      title={portalName ? `${config.label}: ${portalName}` : config.label}
    >
      {config.icon}
    </span>
  )
}
